import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../../shared/services/api.service';
import { ThemeService } from '../../shared/services/theme.service';

@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.css']
})
export class ForgotPasswordComponent implements OnInit {
  emailForm: FormGroup;
  otpForm: FormGroup;
  loading = false;
  step: 'email' | 'otp' | 'done' = 'email';
  submittedEmail = '';
  isDarkMode$ = this.themeService.isDarkMode$;

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private themeService: ThemeService
  ) {
    this.emailForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });
    this.otpForm = this.fb.group({
      otp: ['', [Validators.required, Validators.pattern(/^\d{4}$/)]],
    });
  }

  ngOnInit(): void {
    const initialEmail = String(this.route.snapshot.queryParamMap.get('email') || '').trim().toLowerCase();
    if (initialEmail) {
      this.emailForm.patchValue({ email: initialEmail }, { emitEvent: false });
    }
  }

  toggleTheme(): void {
    this.themeService.toggleDarkMode();
  }

  async requestOtp(): Promise<void> {
    if (this.emailForm.invalid) {
      this.emailForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    const email = String(this.emailForm.value.email || '').trim().toLowerCase();
    try {
      const response = await firstValueFrom(this.apiService.requestPasswordResetOtp({ email }));
      this.submittedEmail = email;
      this.step = 'otp';
      this.snackBar.open(response.message || 'OTP sent to your email', 'Close', { duration: 5000 });
    } catch (error: any) {
      const message = error?.error?.error?.message || 'Could not send OTP. Please try again';
      this.snackBar.open(message, 'Close', { duration: 6000 });
    } finally {
      this.loading = false;
    }
  }

  async verifyOtp(): Promise<void> {
    if (this.otpForm.invalid || !this.submittedEmail) {
      this.otpForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    try {
      const response = await firstValueFrom(
        this.apiService.verifyPasswordResetOtp({
          email: this.submittedEmail,
          otp: String(this.otpForm.value.otp || '').trim(),
        })
      );
      this.step = 'done';
      this.snackBar.open(response.message || 'Password reset email sent', 'Close', { duration: 6000 });
    } catch (error: any) {
      const message = error?.error?.error?.message || 'OTP verification failed';
      this.snackBar.open(message, 'Close', { duration: 6000 });
    } finally {
      this.loading = false;
    }
  }

  backToEmail(): void {
    this.step = 'email';
    this.otpForm.reset();
  }

  async goToLogin(): Promise<void> {
    await this.router.navigate(['/login']);
  }
}
